import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, BookOpen, Gavel, ScrollText, Trash2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";

const LAW_KEY = "aldakheel_fav_laws";
const PRINC_KEY = "aldakheel_fav_principles";

const readIds = (k) => {
  try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; }
};

export default function Saved() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [laws, setLaws] = useState([]);
  const [principles, setPrinciples] = useState([]);
  const [loading, setLoading] = useState(true);

  const t = useMemo(() => ({
    title: isAr ? "محفوظاتي" : "My saved items",
    lead: isAr ? "القوانين ومبادئ التمييز التي حفظتها للرجوع إليها." : "Laws and principles you saved.",
    laws: isAr ? "القوانين واللوائح المحفوظة" : "Saved laws & regulations",
    princ: isAr ? "مبادئ التمييز المحفوظة" : "Saved principles",
    emptyLaws: isAr ? "لا قوانين محفوظة بعد." : "No saved laws yet.",
    emptyPrinc: isAr ? "لا مبادئ محفوظة بعد." : "No saved principles yet.",
    read: isAr ? "قراءة" : "Read",
    remove: isAr ? "إزالة" : "Remove",
    browse: isAr ? "تصفّح المكتبة القانونية" : "Browse the legal library",
    loading: isAr ? "جارِ التحميل..." : "Loading…",
    year: isAr ? "سنة" : "Year",
  }), [isAr]);

  const load = async () => {
    setLoading(true);
    const lawIds = readIds(LAW_KEY);
    const princIds = readIds(PRINC_KEY);
    const [lawDocs, princDocs] = await Promise.all([
      Promise.all(lawIds.map((id) =>
        api.get(`/laws/${id}`).then((r) => ({
          id, title: r.data?.title, law_year: r.data?.law_year,
          category: r.data?.category, document_kind: r.data?.document_kind,
        })).catch(() => null))),
      Promise.all(princIds.map((id) =>
        api.get(`/judgments/${id}`).then((r) => ({
          id, case_number: r.data?.case_number, court: r.data?.court,
          category: r.data?.category,
        })).catch(() => null))),
    ]);
    setLaws(lawDocs.filter(Boolean));
    setPrinciples(princDocs.filter(Boolean));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const removeLaw = (id) => {
    localStorage.setItem(LAW_KEY, JSON.stringify(readIds(LAW_KEY).filter((x) => x !== id)));
    setLaws((l) => l.filter((x) => x.id !== id));
  };
  const removePrinc = (id) => {
    localStorage.setItem(PRINC_KEY, JSON.stringify(readIds(PRINC_KEY).filter((x) => x !== id)));
    setPrinciples((l) => l.filter((x) => x.id !== id));
  };

  return (
    <div data-testid="saved-page" className="max-w-4xl mx-auto px-6 lg:px-10 py-20">
      <div className="flex items-center gap-2 mb-2">
        <Bookmark className="w-6 h-6" style={{ color: "var(--gold-deep)" }} />
        <h1 className="font-display text-3xl lg:text-4xl">{t.title}</h1>
      </div>
      <p className="text-slate-600 mb-10">{t.lead}</p>

      {loading ? (
        <p className="text-slate-500">{t.loading}</p>
      ) : (
        <div className="space-y-12">
          <section data-testid="saved-laws-section">
            <h2 className="eyebrow mb-4" style={{ color: "var(--gold-deep)" }}>
              {t.laws} ({laws.length})
            </h2>
            {laws.length === 0 ? (
              <div className="border border-dashed border-slate-300 p-10 text-center text-slate-500">
                {t.emptyLaws}{" "}
                <Link to="/laws" className="underline">{t.browse}</Link>
              </div>
            ) : (
              <ul className="border border-slate-200 divide-y divide-slate-100 bg-white rounded-sm">
                {laws.map((l) => (
                  <li key={l.id} data-testid={`saved-law-${l.id}`} className="flex items-center justify-between gap-4 p-4">
                    <div className="flex items-start gap-3 min-w-0" dir="rtl">
                      <ScrollText className="w-4 h-4 mt-1 shrink-0 text-slate-400" />
                      <div className="min-w-0">
                        <Link to={`/laws/${l.id}`} className="font-display text-base hover:text-[var(--gold-deep)] transition-colors">
                          {l.title || l.id}
                        </Link>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {l.law_year ? `${t.year} ${l.law_year}` : ""}{l.category ? ` · ${l.category}` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Link to={`/laws/${l.id}`} className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-[0.14em] rounded-sm text-white" style={{ background: "var(--navy)" }}>
                        <BookOpen className="w-3.5 h-3.5" /> {t.read}
                      </Link>
                      <button type="button" onClick={() => removeLaw(l.id)} data-testid={`saved-law-remove-${l.id}`} className="p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" title={t.remove}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section data-testid="saved-principles-section">
            <h2 className="eyebrow mb-4" style={{ color: "var(--gold-deep)" }}>
              {t.princ} ({principles.length})
            </h2>
            {principles.length === 0 ? (
              <div className="border border-dashed border-slate-300 p-10 text-center text-slate-500">
                {t.emptyPrinc}{" "}
                <Link to="/judgments" className="underline">{isAr ? "تصفّح المبادئ" : "Browse principles"}</Link>
              </div>
            ) : (
              <ul className="border border-slate-200 divide-y divide-slate-100 bg-white rounded-sm">
                {principles.map((r) => (
                  <li key={r.id} data-testid={`saved-principle-${r.id}`} className="flex items-center justify-between gap-4 p-4">
                    <div className="flex items-start gap-3 min-w-0" dir="rtl">
                      <Gavel className="w-4 h-4 mt-1 shrink-0" style={{ color: "var(--gold-deep)" }} />
                      <div className="min-w-0">
                        <Link to={`/judgments/${r.id}`} className="font-display text-base hover:text-[var(--gold-deep)] transition-colors">
                          {r.case_number || r.id}
                        </Link>
                        {r.court && <p className="text-xs text-slate-500 mt-0.5">{r.court}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Link to={`/judgments/${r.id}`} className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-[0.14em] rounded-sm text-white" style={{ background: "var(--navy)" }}>
                        <BookOpen className="w-3.5 h-3.5" /> {t.read}
                      </Link>
                      <button type="button" onClick={() => removePrinc(r.id)} data-testid={`saved-principle-remove-${r.id}`} className="p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" title={t.remove}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
